import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import FacturaForm, { TIPO_DOCUMENTO_LABEL } from '../components/FacturaForm';
import { formatFecha } from '../utils/format';
import BotonAyuda from '../components/BotonAyuda';

// ============================================================
// Carga de factura de VENTA a pantalla completa (#34). Antes era un modal
// dentro del estado de cuenta del cliente; una factura tiene demasiados
// campos para una ventanita, y así queda enlazable.
//
// Tres entradas, todas con el cliente ya definido:
//   /clientes/:clienteId/factura/nueva
//   /clientes/:clienteId/factura/:facturaId          (editar)
//   /clientes/:clienteId/nota/:tipo/:afectadaId      (NC/ND sobre una factura)
//
// La compra sigue en el modal del proveedor, con el mismo FacturaForm.
// ============================================================

const TIPO_RUTA = { credito: 'nota_credito', debito: 'nota_debito' };

export default function FacturaVentaForm() {
  const { clienteId, facturaId, tipo, afectadaId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { empresaActivaData } = useAuth();
  const esRestaurante = empresaActivaData?.rubro === 'restaurante';
  const tipoDocumento = tipo ? (TIPO_RUTA[tipo] || 'factura') : 'factura';

  // El cliente puede venir en el state de la navegación (camino normal) o
  // resolverse por id si se entró por link directo o se recargó la página.
  const [entidad, setEntidad] = useState(state?.entidad || null);
  const [facturaAfectada, setFacturaAfectada] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    (async () => {
      if (!entidad) {
        const { data } = await api.get('/clientes/todas');
        const c = data.find((x) => String(x.id) === String(clienteId));
        if (!vivo) return;
        if (!c) { setError('No se encontró el cliente'); return; }
        setEntidad(c);
      }
      if (afectadaId) {
        const { data } = await api.get(`/facturas/${afectadaId}`);
        if (vivo) setFacturaAfectada(data);
      }
    })().catch((err) => vivo && setError(err.response?.data?.error || 'No se pudo abrir el formulario'));
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, afectadaId]);

  const volver = () => navigate('/clientes', { state: { clienteId: Number(clienteId) } });

  const titulo = facturaId
    ? 'Editar factura'
    : tipoDocumento !== 'factura'
      ? TIPO_DOCUMENTO_LABEL[tipoDocumento]
      : 'Nueva factura';

  if (error) {
    return (
      <div className="p-4">
        <button onClick={volver} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
          <ArrowLeft size={16} /> Volver a Clientes
        </button>
        <p className="text-red-600 text-sm mt-4">{error}</p>
      </div>
    );
  }

  // Una nota no se dibuja hasta tener la factura afectada: sus líneas se
  // precargan de ahí.
  const listo = entidad && (!afectadaId || facturaAfectada);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button onClick={volver} title="Volver a Clientes" className="text-slate-400 hover:text-slate-700">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-lg font-semibold text-slate-800">
            {titulo}
            {entidad && <span className="text-slate-400 font-normal"> · {entidad.nombre}</span>}
            <BotonAyuda />
          </h1>
          {facturaAfectada && (
            <p className="text-xs text-slate-500">
              Sobre la factura {facturaAfectada.numero} del {formatFecha(facturaAfectada.fecha)}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border p-4 sm:p-6">
        {listo ? (
          <FacturaForm
            parte="cliente"
            entidad={entidad}
            facturaId={facturaId || null}
            tipoDocumento={tipoDocumento}
            facturaAfectada={facturaAfectada}
            esRestaurante={esRestaurante}
            enPantalla
            onGuardado={volver}
            onCancelar={volver}
          />
        ) : (
          <p className="text-sm text-slate-400 py-6 text-center">Cargando…</p>
        )}
      </div>
    </div>
  );
}
